'use client'

import React, {
  memo,
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  Download,
  FileText,
  Files,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Sparkles,
  X,
} from 'lucide-react'
import { flushSync } from 'react-dom'
import { HiOutlineChatBubbleLeftRight } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { Thumbnail } from '@/components/common/Attachment/Thumbnail'
import Button from '@/components/common/Button'
import { FullScreenViewerContent } from '@/components/common/PdfViewerV2/FullScreenViewer'

import {
  cleanFileName,
  downloadFile,
  getBrowserPdfUrl,
  getBrowserPreviewUrl,
  getFileExtensionFromName,
} from '@/helpers/file'
import { formatStableDate } from '@/helpers/ssr/dates'

import {
  type SolicitationDocumentsFilterKey,
  useSetSolicitationDocumentsPrefs,
  useSolicitationDocumentsPrefs,
} from '@/stores/SolicitationDocumentsPrefsStore'

import type { File as ViewerFile } from '@/types/File'

import { CardV2 } from '../CardV2'
import { getFileTypeDisplayName, type NormalizedFile } from '../transforms'

type FilterKey = SolicitationDocumentsFilterKey
type FileCategory = Exclude<FilterKey, 'all'> | 'other'

interface OpportunityDocumentsCopy {
  emptyMessage?: string
  emptyTitle?: string
  searchLabel?: string
  title?: string
}

interface OpportunityDocumentsSectionProps extends OpportunityDocumentsCopy {
  files: NormalizedFile[]
  persistenceKey?: string
  isHighlighted: boolean
  className?: string
  onSelectFile?: (file: NormalizedFile) => void
}

interface OpportunityDocumentsTabProps extends OpportunityDocumentsCopy {
  files: NormalizedFile[]
  persistenceKey?: string
  selectedFileId: string | null
  onSelectedFileIdChange: (fileId: string) => void
  selectionScrollRequestKey?: number | null
  onSelectionScrollRequestHandled?: () => void
  suppressDefaultListOpen?: boolean
  onAnalyzeFile?: (file: NormalizedFile) => void
  isAnalyzingFile?: boolean
  className?: string
}

type SolicitationDocumentsSectionProps = OpportunityDocumentsSectionProps
type SolicitationDocumentsTabProps = OpportunityDocumentsTabProps

interface DocumentFilteringControls {
  query: string
  onQueryChange: (query: string) => void
  activeFilter: FilterKey
  onFilterChange: (filter: FilterKey) => void
}

interface DocumentsSidebarContentProps extends DocumentFilteringControls {
  counts: Record<FilterKey, number>
  emptyMessage?: string
  emptyTitle?: string
  files: NormalizedFile[]
  hasSourceFiles: boolean
  initialScrollTop?: number | null
  listIdentityKey?: string
  onCollapse: () => void
  onScrollTopChange?: (scrollTop: number) => void
  onSelectFile: (file: NormalizedFile) => void
  searchLabel?: string
  searchId: string
  selectedFileId: string | null
  collapseButtonPlacement?: 'start' | 'end'
  headerLeadingClassName?: string
  showCollapseButton?: boolean
  showToolbar: boolean
  sourceFileCount: number
}

interface AutoFitTitleSize {
  className: string
  fontSizePx: number
}

interface FileCategoryConfig {
  key: FilterKey
  label: string
}

const FILTERS: FileCategoryConfig[] = [
  { key: 'all', label: 'All' },
  { key: 'pdf', label: 'PDF' },
  { key: 'word', label: 'Word' },
  { key: 'excel', label: 'Excel' },
  { key: 'archive', label: 'Archive' },
  { key: 'image', label: 'Image' },
]

const AUTO_FIT_TITLE_SIZES: AutoFitTitleSize[] = [
  { className: 'text-lg leading-6', fontSizePx: 18 },
  { className: 'text-base leading-6', fontSizePx: 16 },
  { className: 'text-sm leading-5', fontSizePx: 14 },
  { className: 'text-xs leading-5', fontSizePx: 12 },
]

const DOCUMENTS_DESKTOP_MEDIA_QUERY = '(min-width: 1280px)'
const shouldOpenDocumentListByDefault = (
  isDesktopViewport: boolean,
  suppressDefaultListOpen: boolean,
) => isDesktopViewport || !suppressDefaultListOpen
const DEFAULT_DOCUMENTS_TITLE = 'Solicitation Documents'
const DEFAULT_EMPTY_DOCUMENTS_TITLE = 'No solicitation documents'
const DEFAULT_EMPTY_DOCUMENTS_MESSAGE =
  'No solicitation documents have been attached to this opportunity.'

function pluralizeFiles(count: number): string {
  return `${count} ${count === 1 ? 'File' : 'Files'}`
}

function normalizeSearchValue(value: string | null | undefined): string {
  return value?.toLowerCase().trim() ?? ''
}

function getDocumentRowElementId(fileId: string): string {
  return `solicitation-document-row-${fileId}`
}

function getFileCategory(file: NormalizedFile): FileCategory {
  const displayType = getFileTypeDisplayName(file.contentType, file.name)
    .toLowerCase()
    .trim()
  const extension = getFileExtensionFromName(file.name).toLowerCase()

  if (displayType === 'pdf' || extension === 'pdf') return 'pdf'

  if (
    ['word', 'word template', 'rtf', 'odt', 'text'].includes(displayType) ||
    ['doc', 'docx', 'dot', 'dotx', 'rtf', 'odt', 'txt'].includes(extension)
  ) {
    return 'word'
  }

  if (
    ['excel', 'excel template', 'csv', 'ods'].includes(displayType) ||
    ['xls', 'xlsx', 'xlt', 'xltx', 'csv', 'ods'].includes(extension)
  ) {
    return 'excel'
  }

  if (
    ['zip', 'rar', '7z', 'gzip', 'tar'].includes(displayType) ||
    ['zip', 'rar', '7z', 'gz', 'tar'].includes(extension)
  ) {
    return 'archive'
  }

  if (
    ['jpeg', 'jpg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff'].includes(
      displayType,
    ) ||
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'tif'].includes(
      extension,
    )
  ) {
    return 'image'
  }

  return 'other'
}

const FileIcon = memo(function FileIcon({
  file,
  compact = false,
  className,
}: {
  file: NormalizedFile
  compact?: boolean
  className?: string
}) {
  return (
    <Thumbnail
      type={getFileExtensionFromName(file.name)}
      className={twMerge(
        'shrink-0 rounded-md',
        compact ? 'h-8 w-8' : 'h-9 w-9',
        className,
      )}
    />
  )
})

function getPreviewUrl(file: NormalizedFile): string | null {
  if (!hasDownloadUrl(file)) return null

  return getBrowserPreviewUrl({
    name: file.name,
    content_type: file.contentType ?? undefined,
    pdf_url: file.pdfUrl ?? undefined,
    url: file.url,
  })
}

function hasDownloadUrl(file: NormalizedFile): boolean {
  return file.url.length > 0
}

function PreviewSurface({
  file,
  mode = 'compact',
}: {
  file: NormalizedFile
  mode?: 'compact' | 'full'
}) {
  const category = getFileCategory(file)
  const previewUrl = getPreviewUrl(file)
  const previewIdentity = previewUrl
    ? `${file.id}:${previewUrl}:${file.pdfUrl ?? ''}`
    : file.id
  const activeViewerPreview = useMemo(() => {
    if (!previewUrl || category === 'image') return null

    const viewerFile: ViewerFile = {
      id: file.id as ViewerFile['id'],
      name: file.name,
      url: file.url,
      pdf_url:
        getBrowserPdfUrl({
          name: file.name,
          content_type: file.contentType ?? undefined,
          pdf_url: file.pdfUrl ?? undefined,
          url: file.url,
        }) ?? undefined,
      content_type: file.contentType ?? undefined,
      size: file.size ?? undefined,
      summary: file.summary ?? undefined,
      created_at: file.createdAt,
      last_modified: file.createdAt,
      index_status: 'finished',
      entities_extraction_status: 'not_extracted',
      type: file.type,
    }

    return {
      fileName: cleanFileName(file.name),
      identity: previewIdentity,
      viewerFile,
    }
  }, [
    category,
    file.contentType,
    file.createdAt,
    file.id,
    file.name,
    file.pdfUrl,
    file.size,
    file.summary,
    file.type,
    file.url,
    previewIdentity,
    previewUrl,
  ])
  const activeViewerPreviewRef = useRef(activeViewerPreview)
  const [visibleViewerPreview, setVisibleViewerPreview] = useState(
    () => activeViewerPreview,
  )

  activeViewerPreviewRef.current = activeViewerPreview

  useEffect(() => {
    setVisibleViewerPreview((currentPreview) => {
      if (!activeViewerPreview) {
        return null
      }
      return currentPreview ?? activeViewerPreview
    })
  }, [activeViewerPreview])

  const markViewerPreviewReady = useCallback(
    (preview: NonNullable<typeof activeViewerPreview>) => {
      if (activeViewerPreviewRef.current?.identity !== preview.identity) return
      setVisibleViewerPreview(preview)
    },
    [],
  )

  if (previewUrl && category === 'image') {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        key={previewIdentity}
        src={previewUrl}
        alt={`Preview of ${cleanFileName(file.name)}`}
        className={twMerge(
          'w-full bg-white object-contain dark:bg-primary-900',
          mode === 'full' ? 'max-h-[720px]' : 'max-h-[560px]',
        )}
      />
    )
  }

  if (activeViewerPreview && visibleViewerPreview) {
    const pendingViewerPreview =
      activeViewerPreview.identity === visibleViewerPreview.identity
        ? null
        : activeViewerPreview
    const renderedViewerPreviews = pendingViewerPreview
      ? [visibleViewerPreview, pendingViewerPreview]
      : [visibleViewerPreview]

    return (
      <div
        aria-label={`Preview of ${activeViewerPreview.fileName}`}
        aria-busy={Boolean(pendingViewerPreview)}
        className={twMerge(
          'relative flex w-full flex-col overflow-hidden bg-primary-100 dark:bg-primary-900',
          mode === 'full' ? 'h-full min-h-[560px]' : 'h-[520px] sm:h-[600px]',
        )}
      >
        {renderedViewerPreviews.map((preview) => {
          const isVisible = preview.identity === visibleViewerPreview.identity
          return (
            <div
              key={preview.identity}
              aria-hidden={!isVisible}
              className={twMerge(
                'flex min-h-0 flex-1 flex-col overflow-hidden',
                isVisible
                  ? 'relative opacity-100'
                  : 'pointer-events-none absolute inset-0 opacity-0',
              )}
            >
              <FullScreenViewerContent
                file={preview.viewerFile}
                toolbar={false}
                onPreviewReady={() => markViewerPreviewReady(preview)}
                onLoadError={() => markViewerPreviewReady(preview)}
                pageScaleMode="fit-width"
                controlsVariant={mode === 'full' ? 'inline' : 'floating'}
                floatingMenuClassName={
                  mode === 'full' ? undefined : 'bottom-3 sm:bottom-4'
                }
              />
            </div>
          )
        })}
      </div>
    )
  }

  return null
}

function AutoFitDocumentTitle({ title }: { title: string }) {
  const titleRef = useRef<HTMLHeadingElement>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [fitState, setFitState] = useState({
    allowWrap: false,
    sizeIndex: 0,
  })

  const updateFitState = useCallback(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return
    }

    const titleElement = titleRef.current
    if (!titleElement) return

    const availableWidth = titleElement.getBoundingClientRect().width
    if (availableWidth <= 0) return

    canvasRef.current ??= document.createElement('canvas')
    const context = canvasRef.current.getContext('2d')
    if (!context) return

    const computedStyle = window.getComputedStyle(titleElement)
    const maxSingleLineWidth = Math.max(0, availableWidth - 1)
    const nextSizeIndex = AUTO_FIT_TITLE_SIZES.findIndex((size) => {
      context.font = `${computedStyle.fontWeight} ${size.fontSizePx}px ${computedStyle.fontFamily}`
      return context.measureText(title).width <= maxSingleLineWidth
    })

    setFitState((currentFitState) => {
      const nextFitState =
        nextSizeIndex === -1
          ? {
              allowWrap: true,
              sizeIndex: AUTO_FIT_TITLE_SIZES.length - 1,
            }
          : { allowWrap: false, sizeIndex: nextSizeIndex }

      if (
        currentFitState.allowWrap === nextFitState.allowWrap &&
        currentFitState.sizeIndex === nextFitState.sizeIndex
      ) {
        return currentFitState
      }

      return nextFitState
    })
  }, [title])

  useEffect(() => {
    updateFitState()

    const titleElement = titleRef.current
    if (!titleElement) return
    if (
      typeof document === 'undefined' ||
      typeof ResizeObserver === 'undefined'
    ) {
      return
    }

    const resizeObserver = new ResizeObserver(updateFitState)
    resizeObserver.observe(titleElement)

    let isCancelled = false
    void document.fonts?.ready.then(() => {
      if (!isCancelled) {
        updateFitState()
      }
    })

    return () => {
      isCancelled = true
      resizeObserver.disconnect()
    }
  }, [updateFitState])

  const selectedSize = AUTO_FIT_TITLE_SIZES[fitState.sizeIndex]

  return (
    <h3
      ref={titleRef}
      title={title}
      className={twMerge(
        'font-semibold text-primary-900 dark:text-primary-100',
        selectedSize.className,
        fitState.allowWrap
          ? 'line-clamp-2 break-words'
          : 'truncate whitespace-nowrap',
      )}
    >
      {title}
    </h3>
  )
}

function useDocumentFiltering(
  files: NormalizedFile[],
  controls?: DocumentFilteringControls,
) {
  const [localQuery, setLocalQuery] = useState('')
  const [localActiveFilter, setLocalActiveFilter] = useState<FilterKey>('all')
  const query = controls?.query ?? localQuery
  const setQuery = controls?.onQueryChange ?? setLocalQuery
  const activeFilter = controls?.activeFilter ?? localActiveFilter
  const setActiveFilter = controls?.onFilterChange ?? setLocalActiveFilter
  const deferredQuery = useDeferredValue(query)
  const showToolbar = files.length > 1

  const counts = useMemo(() => {
    const nextCounts: Record<FilterKey, number> = {
      all: files.length,
      pdf: 0,
      word: 0,
      excel: 0,
      archive: 0,
      image: 0,
    }

    for (const file of files) {
      const category = getFileCategory(file)
      if (category !== 'other') {
        nextCounts[category] += 1
      }
    }

    return nextCounts
  }, [files])

  const effectiveActiveFilter =
    showToolbar && activeFilter !== 'all' && counts[activeFilter] === 0
      ? 'all'
      : activeFilter

  const filteredFiles = useMemo(() => {
    if (!showToolbar) return files

    const normalizedQuery = normalizeSearchValue(deferredQuery)

    return files.filter((file) => {
      const category = getFileCategory(file)
      if (effectiveActiveFilter !== 'all' && category !== effectiveActiveFilter)
        return false
      if (!normalizedQuery) return true

      const searchableText = [
        cleanFileName(file.name),
        getFileTypeDisplayName(file.contentType, file.name),
        file.sizeFormatted,
        formatStableDate(file.createdAt),
        file.summary,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return searchableText.includes(normalizedQuery)
    })
  }, [deferredQuery, effectiveActiveFilter, files, showToolbar])

  return {
    query,
    setQuery,
    activeFilter: effectiveActiveFilter,
    setActiveFilter,
    counts,
    filteredFiles,
    showToolbar,
  }
}

const OpportunityDocumentsSection: React.FC<
  OpportunityDocumentsSectionProps
> = ({
  files,
  persistenceKey,
  isHighlighted,
  className,
  onSelectFile,
  title = DEFAULT_DOCUMENTS_TITLE,
  emptyTitle = DEFAULT_EMPTY_DOCUMENTS_TITLE,
  emptyMessage = DEFAULT_EMPTY_DOCUMENTS_MESSAGE,
  searchLabel = 'Search documents',
}) => {
  const persistedPrefs = useSolicitationDocumentsPrefs(persistenceKey)
  const setPersistedPrefs = useSetSolicitationDocumentsPrefs()

  const setPersistedQuery = useCallback(
    (nextQuery: string) => {
      if (!persistenceKey) return
      setPersistedPrefs(persistenceKey, { query: nextQuery })
    },
    [persistenceKey, setPersistedPrefs],
  )

  const setPersistedActiveFilter = useCallback(
    (nextFilter: FilterKey) => {
      if (!persistenceKey) return
      setPersistedPrefs(persistenceKey, { activeFilter: nextFilter })
    },
    [persistenceKey, setPersistedPrefs],
  )

  const persistedFilteringControls = useMemo<
    DocumentFilteringControls | undefined
  >(() => {
    if (!persistenceKey) return undefined

    return {
      query: persistedPrefs.query,
      onQueryChange: setPersistedQuery,
      activeFilter: persistedPrefs.activeFilter,
      onFilterChange: setPersistedActiveFilter,
    }
  }, [
    persistenceKey,
    persistedPrefs.activeFilter,
    persistedPrefs.query,
    setPersistedActiveFilter,
    setPersistedQuery,
  ])

  const {
    query,
    setQuery,
    activeFilter,
    setActiveFilter,
    counts,
    filteredFiles,
    showToolbar,
  } = useDocumentFiltering(files, persistedFilteringControls)

  const handleDownload = useCallback((file: NormalizedFile) => {
    if (!hasDownloadUrl(file)) return
    void downloadFile(file.url, cleanFileName(file.name))
  }, [])

  const handleSelectFile = useCallback(
    (file: NormalizedFile) => {
      if (persistenceKey) {
        setPersistedPrefs(persistenceKey, { selectedFileId: file.id })
      }
      onSelectFile?.(file)
    },
    [onSelectFile, persistenceKey, setPersistedPrefs],
  )

  return (
    <CardV2
      id="files-section"
      variant="accent"
      className={twMerge(
        'mt-4 transition-all',
        isHighlighted
          ? 'border-accent-400 shadow-[0_0_0_4px_rgba(99,102,241,0.15)] ring-1 ring-accent-400 duration-200 ease-in dark:border-accent-500 dark:ring-accent-500'
          : 'duration-1000 ease-out',
        className,
      )}
    >
      <CardV2.Header
        className={twMerge(
          isHighlighted && 'border-accent-200 dark:border-accent-700',
        )}
      >
        <CardV2.HeaderIcon
          className={twMerge(
            isHighlighted && 'text-accent-600 dark:text-accent-400',
          )}
        >
          <Files />
        </CardV2.HeaderIcon>
        <CardV2.HeaderTitle
          className={twMerge(
            isHighlighted && 'text-accent-700 dark:text-accent-400',
          )}
        >
          {title}
        </CardV2.HeaderTitle>
        <CardV2.HeaderBadge>{pluralizeFiles(files.length)}</CardV2.HeaderBadge>
      </CardV2.Header>

      <CardV2.Content className="space-y-3">
        {showToolbar ? (
          <DocumentsToolbar
            searchId="solicitation-document-preview-search"
            searchLabel={searchLabel}
            query={query}
            onQueryChange={setQuery}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            counts={counts}
          />
        ) : null}
        <CompactFileList
          files={filteredFiles}
          hasSourceFiles={files.length > 0}
          emptyTitle={emptyTitle}
          emptyMessage={emptyMessage}
          selectedFileId={persistedPrefs.selectedFileId}
          onSelectFile={handleSelectFile}
          onDownload={handleDownload}
        />
      </CardV2.Content>
    </CardV2>
  )
}

const OpportunityDocumentsTab: React.FC<OpportunityDocumentsTabProps> = ({
  files,
  persistenceKey,
  selectedFileId,
  onSelectedFileIdChange,
  selectionScrollRequestKey = null,
  onSelectionScrollRequestHandled,
  suppressDefaultListOpen = false,
  onAnalyzeFile,
  isAnalyzingFile = false,
  className,
  emptyTitle = DEFAULT_EMPTY_DOCUMENTS_TITLE,
  emptyMessage = DEFAULT_EMPTY_DOCUMENTS_MESSAGE,
  searchLabel = 'Search documents',
}) => {
  const persistedPrefs = useSolicitationDocumentsPrefs(persistenceKey)
  const setPersistedPrefs = useSetSolicitationDocumentsPrefs()
  const shouldRestoreListScrollRef = useRef(selectionScrollRequestKey === null)

  const setPersistedQuery = useCallback(
    (nextQuery: string) => {
      if (!persistenceKey) return
      setPersistedPrefs(persistenceKey, { query: nextQuery })
    },
    [persistenceKey, setPersistedPrefs],
  )

  const setPersistedActiveFilter = useCallback(
    (nextFilter: FilterKey) => {
      if (!persistenceKey) return
      setPersistedPrefs(persistenceKey, { activeFilter: nextFilter })
    },
    [persistenceKey, setPersistedPrefs],
  )

  const setPersistedScrollTop = useCallback(
    (scrollTop: number) => {
      if (!persistenceKey) return
      setPersistedPrefs(persistenceKey, { scrollTop })
    },
    [persistenceKey, setPersistedPrefs],
  )

  const handleSelectedFileIdChange = useCallback(
    (fileId: string) => {
      if (fileId !== selectedFileId) {
        onSelectedFileIdChange(fileId)
      }
      if (!persistenceKey || fileId === persistedPrefs.selectedFileId) return
      setPersistedPrefs(persistenceKey, { selectedFileId: fileId })
    },
    [
      onSelectedFileIdChange,
      persistedPrefs.selectedFileId,
      persistenceKey,
      selectedFileId,
      setPersistedPrefs,
    ],
  )

  const persistedFilteringControls = useMemo<
    DocumentFilteringControls | undefined
  >(() => {
    if (!persistenceKey) return undefined

    return {
      query: persistedPrefs.query,
      onQueryChange: setPersistedQuery,
      activeFilter: persistedPrefs.activeFilter,
      onFilterChange: setPersistedActiveFilter,
    }
  }, [
    persistenceKey,
    persistedPrefs.activeFilter,
    persistedPrefs.query,
    setPersistedActiveFilter,
    setPersistedQuery,
  ])

  const {
    query,
    setQuery,
    activeFilter,
    setActiveFilter,
    counts,
    filteredFiles,
    showToolbar,
  } = useDocumentFiltering(files, persistedFilteringControls)
  const effectiveSelectedFileId =
    selectedFileId ?? persistedPrefs.selectedFileId

  const selectedFile = useMemo(() => {
    return (
      filteredFiles.find((file) => file.id === effectiveSelectedFileId) ??
      filteredFiles[0] ??
      null
    )
  }, [effectiveSelectedFileId, filteredFiles])
  const [summaryMode, setSummaryMode] = useState<'compact' | 'expanded'>(
    'compact',
  )
  const [isDesktopViewport, setIsDesktopViewport] = useState(true)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    () => suppressDefaultListOpen,
  )

  const updateSidebarCollapsed = useCallback((isCollapsed: boolean) => {
    setIsSidebarCollapsed(isCollapsed)
  }, [])

  const toggleSidebarCollapsed = useCallback(() => {
    setIsSidebarCollapsed((isCollapsed) => !isCollapsed)
  }, [])

  const closeSidebarOnNarrowViewport = useCallback(() => {
    if (isDesktopViewport) return
    updateSidebarCollapsed(true)
  }, [isDesktopViewport, updateSidebarCollapsed])

  useEffect(() => {
    if (
      selectedFile &&
      (selectedFile.id !== selectedFileId ||
        selectedFile.id !== persistedPrefs.selectedFileId)
    ) {
      handleSelectedFileIdChange(selectedFile.id)
    }
  }, [
    handleSelectedFileIdChange,
    persistedPrefs.selectedFileId,
    selectedFile,
    selectedFileId,
  ])

  useEffect(() => {
    setSummaryMode('compact')
  }, [selectedFile?.id])

  useEffect(() => {
    const desktopMediaQuery = window.matchMedia(DOCUMENTS_DESKTOP_MEDIA_QUERY)

    const syncSidebarForViewport = () => {
      const isDesktop = desktopMediaQuery.matches
      startTransition(() => {
        setIsDesktopViewport(isDesktop)
        setIsSidebarCollapsed(
          !shouldOpenDocumentListByDefault(isDesktop, suppressDefaultListOpen),
        )
      })
    }

    syncSidebarForViewport()
    desktopMediaQuery.addEventListener('change', syncSidebarForViewport)

    return () => {
      desktopMediaQuery.removeEventListener('change', syncSidebarForViewport)
    }
  }, [suppressDefaultListOpen])

  useEffect(() => {
    if (selectionScrollRequestKey === null || !selectedFileId) return

    shouldRestoreListScrollRef.current = false

    const timeout = window.setTimeout(() => {
      document
        .getElementById(getDocumentRowElementId(selectedFileId))
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      shouldRestoreListScrollRef.current = true
      onSelectionScrollRequestHandled?.()
    }, 120)

    return () => {
      window.clearTimeout(timeout)
      shouldRestoreListScrollRef.current = true
    }
  }, [
    onSelectionScrollRequestHandled,
    selectedFileId,
    selectionScrollRequestKey,
  ])

  const handleDownload = useCallback((file: NormalizedFile) => {
    if (!hasDownloadUrl(file)) return
    void downloadFile(file.url, cleanFileName(file.name))
  }, [])

  const isNarrowDrawerOpen = !isDesktopViewport && !isSidebarCollapsed
  const sidebarContent = (
    <DocumentsSidebarContent
      searchId="solicitation-document-tab-search"
      searchLabel={searchLabel}
      query={query}
      onQueryChange={setQuery}
      activeFilter={activeFilter}
      onFilterChange={setActiveFilter}
      counts={counts}
      files={filteredFiles}
      hasSourceFiles={files.length > 0}
      emptyTitle={emptyTitle}
      emptyMessage={emptyMessage}
      selectedFileId={selectedFile?.id ?? null}
      onSelectFile={(file) => {
        handleSelectedFileIdChange(file.id)
        closeSidebarOnNarrowViewport()
      }}
      initialScrollTop={
        shouldRestoreListScrollRef.current ? persistedPrefs.scrollTop : null
      }
      listIdentityKey={persistenceKey}
      onScrollTopChange={persistenceKey ? setPersistedScrollTop : undefined}
      onCollapse={() => updateSidebarCollapsed(true)}
      collapseButtonPlacement={isDesktopViewport ? 'end' : 'start'}
      headerLeadingClassName={
        !isDesktopViewport ? 'min-h-9 gap-2 pl-40' : undefined
      }
      showCollapseButton={isDesktopViewport}
      showToolbar={showToolbar}
      sourceFileCount={files.length}
    />
  )
  const mobileDrawerClickAway = isNarrowDrawerOpen ? (
    <button
      type="button"
      tabIndex={-1}
      aria-label="Close document list"
      onClick={() => updateSidebarCollapsed(true)}
      className="absolute inset-0 z-20 hidden cursor-default bg-transparent focus:outline-none sm:block"
    />
  ) : null
  const mobileDrawer = !isDesktopViewport ? (
    <div
      aria-hidden={isSidebarCollapsed}
      className={twMerge(
        'absolute inset-0 z-30 flex min-h-0 flex-col overflow-hidden bg-white transition-transform duration-200 ease-out will-change-transform motion-reduce:transition-none dark:bg-primary-900 sm:bottom-3 sm:left-0 sm:right-auto sm:top-0 sm:w-[420px] sm:rounded-r-lg sm:border-r sm:border-primary-200 sm:shadow-md sm:dark:border-primary-700',
        isSidebarCollapsed
          ? 'pointer-events-none -translate-x-[calc(100%+1rem)]'
          : 'translate-x-0',
      )}
    >
      {sidebarContent}
    </div>
  ) : null
  const isMobileDocumentListToggleVisible = !isDesktopViewport
  const MobileDocumentListToggleIcon = isSidebarCollapsed
    ? PanelLeftOpen
    : PanelLeftClose
  const mobileDocumentListToggleButton = isMobileDocumentListToggleVisible ? (
    <button
      type="button"
      onClick={toggleSidebarCollapsed}
      aria-label={
        isSidebarCollapsed ? 'Show document list' : 'Hide document list'
      }
      className={twMerge(
        'absolute left-3 top-3 z-40 flex h-9 w-36 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border px-3 text-sm font-medium shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-200 dark:focus-visible:ring-accent-700',
        isSidebarCollapsed ? 'sm:left-4 sm:top-4' : 'sm:left-3 sm:top-3',
        isSidebarCollapsed
          ? 'border-accent-500 bg-accent-400 text-white hover:bg-accent-500 hover:text-white dark:border-accent-500 dark:bg-accent-400/80 dark:text-white dark:hover:bg-accent-400 dark:hover:text-white'
          : 'border-primary-200 bg-white text-primary-500 hover:bg-primary-50 hover:text-primary-800 dark:border-primary-700 dark:bg-primary-900 dark:text-primary-400 dark:hover:bg-primary-800 dark:hover:text-primary-100',
      )}
    >
      <MobileDocumentListToggleIcon className="h-4 w-4" />
      <span>
        {isSidebarCollapsed
          ? `View ${pluralizeFiles(files.length)}`
          : 'Hide files'}
      </span>
    </button>
  ) : null
  if (files.length === 0) {
    return (
      <div id="solicitation-documents-tab-panel" className={className}>
        <NoSolicitationDocumentsState
          emptyTitle={emptyTitle}
          emptyMessage={emptyMessage}
        />
      </div>
    )
  }

  return (
    <div
      id="solicitation-documents-tab-panel"
      className={twMerge(
        'flex h-[720px] min-h-[720px] flex-col overflow-hidden rounded-lg border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900 xl:h-[calc(100dvh-112px)] xl:min-h-[760px]',
        className,
      )}
    >
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        {isDesktopViewport ? (
          <div
            aria-hidden={isSidebarCollapsed}
            className={twMerge(
              'flex min-h-0 shrink-0 flex-col overflow-hidden border-r border-primary-200 transition-[width,opacity,border-color] duration-200 ease-out motion-reduce:transition-none dark:border-primary-700',
              isSidebarCollapsed
                ? 'pointer-events-none w-0 border-transparent opacity-0 dark:border-transparent'
                : 'w-[340px] opacity-100',
            )}
          >
            {sidebarContent}
          </div>
        ) : null}
        {mobileDrawerClickAway}
        {mobileDrawer}
        {mobileDocumentListToggleButton}

        <SolicitationDocumentDetail
          file={selectedFile}
          hasSourceFiles={files.length > 0}
          emptyTitle={emptyTitle}
          emptyMessage={emptyMessage}
          onAnalyzeFile={onAnalyzeFile}
          isAnalyzingFile={isAnalyzingFile}
          onDownloadFile={handleDownload}
          documentCount={files.length}
          summaryMode={summaryMode}
          onSummaryModeChange={setSummaryMode}
          isDocumentListButtonVisible={isDesktopViewport && isSidebarCollapsed}
          isDocumentListOpen={!isSidebarCollapsed}
          hasPersistentDocumentListToggle={isMobileDocumentListToggleVisible}
          onToggleSidebar={toggleSidebarCollapsed}
          className={twMerge(
            'min-h-0 flex-1 rounded-none border-0 shadow-none transition-opacity duration-150',
            isNarrowDrawerOpen &&
              'pointer-events-none opacity-0 sm:pointer-events-auto sm:opacity-100',
          )}
        />
      </div>
    </div>
  )
}

function DocumentsSidebarContent({
  searchId,
  searchLabel,
  query,
  onQueryChange,
  activeFilter,
  onFilterChange,
  counts,
  emptyTitle,
  emptyMessage,
  files,
  hasSourceFiles,
  selectedFileId,
  onSelectFile,
  initialScrollTop,
  listIdentityKey,
  onScrollTopChange,
  onCollapse,
  collapseButtonPlacement = 'end',
  headerLeadingClassName,
  showCollapseButton = true,
  showToolbar,
  sourceFileCount,
}: DocumentsSidebarContentProps) {
  const collapseButton = showCollapseButton ? (
    <button
      type="button"
      onClick={onCollapse}
      aria-label="Hide files"
      className={twMerge(
        'flex h-7 shrink-0 items-center justify-center rounded-md text-primary-500 transition-colors hover:bg-primary-100 hover:text-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-200 dark:text-primary-400 dark:hover:bg-primary-800 dark:hover:text-primary-100 dark:focus-visible:ring-accent-700',
        collapseButtonPlacement === 'start'
          ? 'gap-1.5 px-2 text-sm font-medium'
          : 'w-7',
      )}
    >
      <PanelLeftClose className="h-4 w-4" />
      {collapseButtonPlacement === 'start' ? <span>Hide files</span> : null}
    </button>
  ) : null

  return (
    <>
      <div className="shrink-0 border-b border-primary-100 bg-white p-3 dark:border-primary-800 dark:bg-primary-900">
        <div className="mb-2 flex min-w-0 items-center justify-between gap-2">
          <div
            className={twMerge(
              'flex min-w-0 items-center gap-4',
              headerLeadingClassName,
            )}
          >
            {collapseButtonPlacement === 'start' && collapseButton
              ? collapseButton
              : null}
            <Files className="h-4 w-4 shrink-0 text-accent-500 dark:text-accent-400" />
            <div className="truncate text-sm font-semibold text-primary-900 dark:text-primary-100">
              {files.length === sourceFileCount
                ? pluralizeFiles(sourceFileCount)
                : `${files.length} of ${pluralizeFiles(sourceFileCount)}`}
            </div>
          </div>
          {collapseButtonPlacement === 'end' ? collapseButton : null}
        </div>
        {showToolbar ? (
          <DocumentsToolbar
            searchId={searchId}
            searchLabel={searchLabel}
            query={query}
            onQueryChange={onQueryChange}
            activeFilter={activeFilter}
            onFilterChange={onFilterChange}
            counts={counts}
          />
        ) : (
          <div className="text-sm text-primary-500 dark:text-primary-400">
            {pluralizeFiles(sourceFileCount)}
          </div>
        )}
      </div>

      <SelectableFileList
        files={files}
        hasSourceFiles={hasSourceFiles}
        emptyTitle={emptyTitle}
        emptyMessage={emptyMessage}
        selectedFileId={selectedFileId}
        onSelectFile={onSelectFile}
        initialScrollTop={initialScrollTop}
        listIdentityKey={listIdentityKey}
        onScrollTopChange={onScrollTopChange}
      />
    </>
  )
}

function DocumentsToolbar({
  searchId,
  searchLabel = 'Search documents',
  query,
  onQueryChange,
  activeFilter,
  onFilterChange,
  counts,
}: {
  searchId: string
  searchLabel?: string
  query: string
  onQueryChange: (query: string) => void
  activeFilter: FilterKey
  onFilterChange: (filter: FilterKey) => void
  counts: Record<FilterKey, number>
}) {
  const visibleFilters = FILTERS.filter(
    (filter) => filter.key === 'all' || counts[filter.key] > 0,
  )

  return (
    <div className="flex flex-col gap-2">
      <div className="relative w-full">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-primary-400 dark:text-primary-500" />
        <label htmlFor={searchId} className="sr-only">
          {searchLabel}
        </label>
        <input
          id={searchId}
          type="text"
          role="searchbox"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Search files..."
          autoComplete="off"
          spellCheck={false}
          className="h-8 w-full rounded-md border border-primary-200 bg-white pl-8 pr-10 text-sm text-primary-900 outline-none transition placeholder:text-primary-400 focus:border-accent-300 focus:ring-2 focus:ring-accent-100 dark:border-primary-700 dark:bg-primary-900 dark:text-primary-100 dark:placeholder:text-primary-500 dark:focus:border-accent-500 dark:focus:ring-accent-400/40"
        />
        {query ? (
          <button
            type="button"
            onClick={() => onQueryChange('')}
            className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-primary-400 transition hover:text-primary-700 dark:text-primary-500 dark:hover:text-primary-200"
            aria-label="Clear file search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1 pb-px">
        {visibleFilters.map((filter) => {
          const isActive = activeFilter === filter.key
          return (
            <button
              key={filter.key}
              type="button"
              onClick={() => onFilterChange(filter.key)}
              className={twMerge(
                'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition',
                isActive
                  ? 'border-accent-200 bg-accent-50 text-accent-700 dark:border-accent-700 dark:bg-accent-950/30 dark:text-accent-300'
                  : 'border-primary-200 bg-white text-primary-600 hover:border-primary-300 hover:bg-primary-50 dark:border-primary-700 dark:bg-primary-900 dark:text-primary-300 dark:hover:border-primary-600 dark:hover:bg-primary-800',
              )}
            >
              {filter.label}
              <span
                className={twMerge(
                  'font-mono text-xs',
                  isActive
                    ? 'text-accent-500 dark:text-accent-300'
                    : 'text-primary-400 dark:text-primary-500',
                )}
              >
                {counts[filter.key]}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function CompactFileList({
  files,
  hasSourceFiles,
  emptyTitle,
  emptyMessage,
  selectedFileId,
  onSelectFile,
  onDownload,
}: {
  files: NormalizedFile[]
  hasSourceFiles: boolean
  emptyTitle?: string
  emptyMessage?: string
  selectedFileId?: string | null
  onSelectFile?: (file: NormalizedFile) => void
  onDownload: (file: NormalizedFile) => void
}) {
  if (files.length === 0) {
    return (
      <NoSolicitationDocumentsState
        hasSourceFiles={hasSourceFiles}
        emptyTitle={emptyTitle}
        emptyMessage={emptyMessage}
      />
    )
  }

  const hasInternalOverflow = files.length > 8

  return (
    <div
      className={twMerge(
        'divide-y divide-primary-200 dark:divide-primary-700',
        hasInternalOverflow && '-mr-4 max-h-[520px] overflow-y-auto pr-4',
      )}
    >
      {files.map((file) => (
        <CompactDocumentRow
          key={file.id}
          file={file}
          isSelected={selectedFileId === file.id}
          onSelectFile={onSelectFile}
          onDownload={onDownload}
        />
      ))}
    </div>
  )
}

function CompactDocumentRow({
  file,
  isSelected,
  onSelectFile,
  onDownload,
}: {
  file: NormalizedFile
  isSelected: boolean
  onSelectFile?: (file: NormalizedFile) => void
  onDownload: (file: NormalizedFile) => void
}) {
  const canDownload = hasDownloadUrl(file)

  return (
    <div className="flex items-start gap-3 py-3">
      <button
        type="button"
        onClick={() => onSelectFile?.(file)}
        className="group flex min-w-0 flex-1 items-start gap-3 text-left focus:outline-none focus-visible:outline-none"
      >
        <FileIcon file={file} />
        <div className="min-w-0 flex-1">
          <div
            title={file.name}
            className={twMerge(
              'truncate text-sm font-semibold transition-none duration-0',
              isSelected
                ? 'text-accent-700 dark:text-accent-300'
                : 'text-primary-900 group-hover:text-blue-600 dark:text-primary-100 dark:group-hover:text-blue-400',
            )}
          >
            {cleanFileName(file.name)}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-primary-500 dark:text-primary-400">
            <span>{getFileTypeDisplayName(file.contentType, file.name)}</span>
            <span>{file.sizeFormatted ?? '-'}</span>
            <span>{formatStableDate(file.createdAt) ?? '-'}</span>
          </div>
          <p className="mt-1 line-clamp-2 text-sm leading-5 text-primary-500 dark:text-primary-400">
            {file.summary || 'No AI summary available for this file.'}
          </p>
        </div>
      </button>
      <Button
        type="button"
        variant="ghost-compact"
        onClick={() => onDownload(file)}
        disabled={!canDownload}
        title={canDownload ? undefined : 'Download unavailable'}
        className="mt-1 h-7 w-7 shrink-0 px-0 text-primary-500 hover:bg-transparent hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-100"
        aria-label={`Download ${cleanFileName(file.name)}`}
      >
        <Download className="h-3.5 w-3.5" />
      </Button>
    </div>
  )
}

function SelectableFileList({
  files,
  hasSourceFiles,
  emptyTitle,
  emptyMessage,
  selectedFileId,
  onSelectFile,
  initialScrollTop,
  listIdentityKey,
  onScrollTopChange,
}: {
  files: NormalizedFile[]
  hasSourceFiles: boolean
  emptyTitle?: string
  emptyMessage?: string
  selectedFileId: string | null
  onSelectFile: (file: NormalizedFile) => void
  initialScrollTop?: number | null
  listIdentityKey?: string
  onScrollTopChange?: (scrollTop: number) => void
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const hasAppliedInitialScrollRef = useRef(false)
  const pendingScrollTopRef = useRef<number | null>(null)
  const scrollAnimationFrameRef = useRef<number | null>(null)

  useEffect(() => {
    hasAppliedInitialScrollRef.current = false
  }, [listIdentityKey])

  useEffect(() => {
    if (hasAppliedInitialScrollRef.current) return

    hasAppliedInitialScrollRef.current = true
    if (initialScrollTop == null) return

    const timeout = window.setTimeout(() => {
      const container = scrollContainerRef.current
      if (!container) return

      const maxScrollTop = Math.max(
        0,
        container.scrollHeight - container.clientHeight,
      )
      container.scrollTop = Math.min(initialScrollTop, maxScrollTop)
    }, 160)

    return () => window.clearTimeout(timeout)
  }, [initialScrollTop, listIdentityKey])

  useEffect(() => {
    return () => {
      if (scrollAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollAnimationFrameRef.current)
      }
    }
  }, [])

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!onScrollTopChange) return

      pendingScrollTopRef.current = Math.max(
        0,
        Math.round(event.currentTarget.scrollTop),
      )

      if (scrollAnimationFrameRef.current !== null) return

      scrollAnimationFrameRef.current = window.requestAnimationFrame(() => {
        scrollAnimationFrameRef.current = null
        const nextScrollTop = pendingScrollTopRef.current
        pendingScrollTopRef.current = null

        if (nextScrollTop !== null) {
          onScrollTopChange(nextScrollTop)
        }
      })
    },
    [onScrollTopChange],
  )

  if (files.length === 0) {
    return (
      <div className="p-3">
        <NoSolicitationDocumentsState
          hasSourceFiles={hasSourceFiles}
          emptyTitle={emptyTitle}
          emptyMessage={emptyMessage}
        />
      </div>
    )
  }

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleScroll}
      className="min-h-0 flex-1 divide-y divide-primary-200 overflow-y-auto dark:divide-primary-700"
    >
      {files.map((file) => {
        const isSelected = selectedFileId === file.id
        return (
          <button
            key={file.id}
            id={getDocumentRowElementId(file.id)}
            type="button"
            onClick={() => onSelectFile(file)}
            className={twMerge(
              'group flex w-full items-start gap-3 px-3 py-3 text-left transition-none duration-0 focus:outline-none focus-visible:outline-none',
              isSelected
                ? 'bg-accent-50/50 dark:bg-accent-950/20'
                : 'bg-transparent hover:bg-transparent',
            )}
          >
            <FileIcon file={file} compact />
            <div className="min-w-0 flex-1">
              <div
                title={file.name}
                className={twMerge(
                  'line-clamp-2 text-sm font-semibold transition-none duration-0',
                  isSelected
                    ? 'text-accent-700 dark:text-accent-300'
                    : 'text-primary-900 group-hover:text-blue-600 dark:text-primary-100 dark:group-hover:text-blue-400',
                )}
              >
                {cleanFileName(file.name)}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-primary-500 dark:text-primary-400">
                <span>
                  {getFileTypeDisplayName(file.contentType, file.name)}
                </span>
                <span>{file.sizeFormatted ?? '-'}</span>
                <span>{formatStableDate(file.createdAt) ?? '-'}</span>
              </div>
            </div>
          </button>
        )
      })}
    </div>
  )
}

function SolicitationDocumentDetail({
  file,
  hasSourceFiles,
  emptyTitle = DEFAULT_EMPTY_DOCUMENTS_TITLE,
  emptyMessage = DEFAULT_EMPTY_DOCUMENTS_MESSAGE,
  onAnalyzeFile,
  isAnalyzingFile,
  onDownloadFile,
  documentCount,
  summaryMode,
  onSummaryModeChange,
  isDocumentListButtonVisible,
  isDocumentListOpen,
  hasPersistentDocumentListToggle,
  onToggleSidebar,
  className,
}: {
  file: NormalizedFile | null
  hasSourceFiles: boolean
  emptyTitle?: string
  emptyMessage?: string
  onAnalyzeFile?: (file: NormalizedFile) => void
  isAnalyzingFile: boolean
  onDownloadFile: (file: NormalizedFile) => void
  documentCount: number
  summaryMode: 'compact' | 'expanded'
  onSummaryModeChange: (mode: 'compact' | 'expanded') => void
  isDocumentListButtonVisible: boolean
  isDocumentListOpen: boolean
  hasPersistentDocumentListToggle: boolean
  onToggleSidebar: () => void
  className?: string
}) {
  if (!file) {
    return (
      <NoSolicitationDocumentsState
        hasSourceFiles={hasSourceFiles}
        emptyTitle={emptyTitle}
        emptyMessage={emptyMessage}
      />
    )
  }

  const hasPreview = getPreviewUrl(file) !== null
  const canDownload = hasDownloadUrl(file)
  const fileMetadata = (
    <>
      <span>{getFileTypeDisplayName(file.contentType, file.name)}</span>
      <span>{file.sizeFormatted ?? '-'}</span>
      <span>{formatStableDate(file.createdAt) ?? '-'}</span>
    </>
  )
  const DocumentListToggleIcon = isDocumentListOpen
    ? PanelLeftClose
    : PanelLeftOpen
  const renderDocumentListToggleButton = (
    className?: string,
    iconClassName?: string,
    label?: string,
  ) => (
    <button
      type="button"
      onClick={onToggleSidebar}
      aria-label={
        isDocumentListOpen ? 'Hide document list' : 'Show document list'
      }
      className={twMerge(
        'flex shrink-0 items-center justify-center gap-2 self-start rounded-md text-sm text-primary-500 transition-colors hover:bg-primary-100 hover:text-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-200 dark:text-primary-400 dark:hover:bg-primary-800 dark:hover:text-primary-100 dark:focus-visible:ring-accent-700',
        className,
      )}
    >
      <DocumentListToggleIcon className={twMerge('h-5 w-5', iconClassName)} />
      <span className="font-medium">
        {label ?? pluralizeFiles(documentCount)}
      </span>
    </button>
  )
  return (
    <div
      className={twMerge(
        'flex h-full min-w-0 flex-col overflow-hidden rounded-lg border border-primary-200 bg-white dark:border-primary-700 dark:bg-primary-900',
        className,
      )}
    >
      <div
        className={twMerge(
          'border-b border-primary-100 px-3 py-2.5 dark:border-primary-800 sm:px-4 sm:py-4',
          hasPersistentDocumentListToggle && 'pt-14 sm:pt-4',
        )}
      >
        {hasPersistentDocumentListToggle ? (
          <div className="-mx-3 mb-3 border-t border-primary-100 dark:border-primary-800 sm:hidden" />
        ) : null}
        <div className="sm:hidden">
          <AutoFitDocumentTitle title={cleanFileName(file.name)} />
          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-primary-500 dark:text-primary-400">
            {fileMetadata}
          </div>

          <div className="mt-2 flex flex-col gap-2">
            {onAnalyzeFile ? (
              <Button
                type="button"
                variant="outline-compact"
                onClick={() => onAnalyzeFile(file)}
                isLoading={isAnalyzingFile}
                disabled={isAnalyzingFile}
                className="h-9 w-full min-w-0 justify-center gap-2 border-accent-200 bg-accent-50 px-3 text-sm font-medium text-accent-700 hover:border-accent-300 hover:bg-accent-100 dark:border-accent-800 dark:bg-accent-950/30 dark:text-accent-300 dark:hover:bg-accent-950/50"
              >
                <HiOutlineChatBubbleLeftRight className="h-4 w-4" />
                Analyze with Sweetspot AI
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline-compact"
              onClick={() => onDownloadFile(file)}
              disabled={!canDownload}
              title={canDownload ? undefined : 'Download unavailable'}
              className="h-9 w-full justify-center gap-2 px-3 text-sm font-medium"
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
          </div>
        </div>

        <div className="hidden min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-3 sm:grid">
          <div
            className={twMerge(
              'flex min-w-0 flex-wrap items-center gap-2',
              hasPersistentDocumentListToggle && 'pl-[9.5rem]',
            )}
          >
            {isDocumentListButtonVisible
              ? renderDocumentListToggleButton(
                  'h-9 border border-primary-200 bg-white px-3 shadow-sm hover:bg-primary-50 dark:border-primary-700 dark:bg-primary-900 dark:hover:bg-primary-800',
                )
              : null}
            {onAnalyzeFile ? (
              <Button
                type="button"
                variant="outline-compact"
                onClick={() => onAnalyzeFile(file)}
                isLoading={isAnalyzingFile}
                disabled={isAnalyzingFile}
                className="h-9 min-w-0 justify-center gap-2 border-accent-200 bg-accent-50 px-3 text-sm font-medium text-accent-700 hover:border-accent-300 hover:bg-accent-100 dark:border-accent-800 dark:bg-accent-950/30 dark:text-accent-300 dark:hover:bg-accent-950/50"
              >
                <HiOutlineChatBubbleLeftRight className="h-4 w-4" />
                Analyze with Sweetspot AI
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline-compact"
              onClick={() => onDownloadFile(file)}
              disabled={!canDownload}
              title={canDownload ? undefined : 'Download unavailable'}
              className="h-9 justify-center gap-2 px-3 text-sm font-medium"
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 self-center text-right text-sm text-primary-500 dark:text-primary-400">
            {fileMetadata}
          </div>

          <div className="col-span-2 flex min-w-0 items-center gap-3">
            <FileIcon file={file} compact className="shrink-0" />
            <div className="min-w-0 flex-1">
              <AutoFitDocumentTitle title={cleanFileName(file.name)} />
            </div>
          </div>
        </div>
      </div>

      <DocumentSummaryStrip
        file={file}
        mode={summaryMode}
        onModeChange={onSummaryModeChange}
      />

      {hasPreview ? (
        <div className="min-h-0 flex-1 bg-white dark:bg-primary-900">
          <PreviewSurface file={file} mode="full" />
        </div>
      ) : (
        <NoDocumentPreviewState
          file={file}
          onAnalyzeFile={onAnalyzeFile}
          isAnalyzingFile={isAnalyzingFile}
          onDownloadFile={onDownloadFile}
        />
      )}
    </div>
  )
}

function DocumentSummaryStrip({
  file,
  mode,
  onModeChange,
}: {
  file: NormalizedFile
  mode: 'compact' | 'expanded'
  onModeChange: (mode: 'compact' | 'expanded') => void
}) {
  const summaryRef = useRef<HTMLDivElement>(null)
  const compactMeasurementRef = useRef<HTMLDivElement>(null)
  const expandedMeasurementRef = useRef<HTMLDivElement>(null)
  const animationFrameRef = useRef<number | null>(null)
  const pendingModeRef = useRef<'compact' | 'expanded' | null>(null)
  const modeRef = useRef(mode)
  const [animatedHeight, setAnimatedHeight] = useState<number | null>(null)
  const [renderMode, setRenderMode] = useState(mode)
  const [isAnimating, setIsAnimating] = useState(false)
  const [isSummaryExpandable, setIsSummaryExpandable] = useState(false)
  const isCompact = renderMode === 'compact'

  modeRef.current = mode

  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [])

  useEffect(() => {
    pendingModeRef.current = null
    setAnimatedHeight(null)
    setIsAnimating(false)
    setRenderMode(modeRef.current)
  }, [file.id])

  useEffect(() => {
    if (!isAnimating) {
      setRenderMode(mode)
    }
  }, [isAnimating, mode])

  const updateSummaryExpandable = useCallback(() => {
    const compactElement = compactMeasurementRef.current
    const expandedElement = expandedMeasurementRef.current
    if (!compactElement || !expandedElement) return

    const compactHeight = compactElement.getBoundingClientRect().height
    const expandedHeight = expandedElement.getBoundingClientRect().height
    const nextIsSummaryExpandable = expandedHeight > compactHeight + 1

    setIsSummaryExpandable((currentIsSummaryExpandable) =>
      currentIsSummaryExpandable === nextIsSummaryExpandable
        ? currentIsSummaryExpandable
        : nextIsSummaryExpandable,
    )
  }, [])

  useLayoutEffect(() => {
    updateSummaryExpandable()

    if (typeof ResizeObserver === 'undefined') return

    const resizeObserver = new ResizeObserver(updateSummaryExpandable)
    if (summaryRef.current) resizeObserver.observe(summaryRef.current)
    if (compactMeasurementRef.current) {
      resizeObserver.observe(compactMeasurementRef.current)
    }
    if (expandedMeasurementRef.current) {
      resizeObserver.observe(expandedMeasurementRef.current)
    }

    return () => resizeObserver.disconnect()
  }, [file.id, file.summary, updateSummaryExpandable])

  const handleToggleMode = useCallback(() => {
    if (isAnimating || !isSummaryExpandable) return

    const nextMode = renderMode === 'compact' ? 'expanded' : 'compact'
    const summaryElement = summaryRef.current
    const targetElement =
      nextMode === 'compact'
        ? compactMeasurementRef.current
        : expandedMeasurementRef.current

    if (
      !summaryElement ||
      !targetElement ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      onModeChange(nextMode)
      setRenderMode(nextMode)
      return
    }

    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current)
    }

    const currentHeight = summaryElement.getBoundingClientRect().height
    const targetHeight = targetElement.getBoundingClientRect().height

    if (Math.abs(currentHeight - targetHeight) < 1) {
      onModeChange(nextMode)
      setRenderMode(nextMode)
      return
    }

    pendingModeRef.current = nextMode
    setIsAnimating(true)

    flushSync(() => {
      setAnimatedHeight(currentHeight)
    })
    if (nextMode === 'expanded') {
      flushSync(() => {
        setRenderMode(nextMode)
      })
    }

    animationFrameRef.current = window.requestAnimationFrame(() => {
      animationFrameRef.current = null
      setAnimatedHeight(targetHeight)
    })
  }, [isAnimating, isSummaryExpandable, onModeChange, renderMode])

  const summaryAction = isSummaryExpandable ? (
    <button
      type="button"
      onClick={handleToggleMode}
      disabled={isAnimating}
      className="inline-flex h-7 shrink-0 items-center rounded px-2 text-sm font-semibold text-accent-700 hover:text-accent-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-200 disabled:cursor-default disabled:opacity-70 dark:text-accent-300 dark:hover:text-accent-200 dark:focus-visible:ring-accent-700"
    >
      {isCompact ? 'Expand' : 'Collapse'}
    </button>
  ) : null

  return (
    <div className="relative isolate">
      <DocumentSummaryStripContent
        ref={summaryRef}
        file={file}
        mode={renderMode}
        className="relative z-10 overflow-hidden transition-[height] duration-200 ease-out"
        style={
          animatedHeight === null
            ? undefined
            : { height: `${animatedHeight}px` }
        }
        onTransitionEnd={(event) => {
          if (
            event.target !== event.currentTarget ||
            event.propertyName !== 'height'
          ) {
            return
          }

          const pendingMode = pendingModeRef.current
          if (pendingMode) {
            setRenderMode(pendingMode)
            onModeChange(pendingMode)
            pendingModeRef.current = null
          }
          setAnimatedHeight(null)
          setIsAnimating(false)
        }}
        action={summaryAction}
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 opacity-0"
      >
        <DocumentSummaryStripContent
          ref={compactMeasurementRef}
          file={file}
          mode="compact"
          action={<SummaryMeasurementAction label="Expand" />}
        />
        <DocumentSummaryStripContent
          ref={expandedMeasurementRef}
          file={file}
          mode="expanded"
          action={<SummaryMeasurementAction label="Collapse" />}
        />
      </div>
    </div>
  )
}

const DocumentSummaryStripContent = React.forwardRef<
  HTMLDivElement,
  {
    action: React.ReactNode
    className?: string
    file: NormalizedFile
    mode: 'compact' | 'expanded'
    onTransitionEnd?: React.TransitionEventHandler<HTMLDivElement>
    style?: React.CSSProperties
  }
>(function DocumentSummaryStripContent(
  { action, className, file, mode, onTransitionEnd, style },
  ref,
) {
  const isCompact = mode === 'compact'

  return (
    <div
      ref={ref}
      onTransitionEnd={onTransitionEnd}
      className={twMerge(
        'flex flex-col gap-1.5 border-b border-primary-100 bg-white px-3 py-2.5 dark:border-primary-800 dark:bg-primary-900 sm:flex-row sm:items-start sm:gap-3 sm:px-4 sm:py-3',
        className,
      )}
      style={style}
    >
      <div className="flex min-w-0 items-center justify-between gap-3 sm:contents">
        <div className="flex h-5 shrink-0 items-center gap-1.5 text-xs font-bold uppercase leading-5 tracking-wider text-accent-600 dark:text-accent-400 sm:h-6 sm:text-sm sm:leading-6">
          <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          AI Summary
        </div>
        {action ? (
          <div className="relative z-10 shrink-0 sm:hidden">{action}</div>
        ) : null}
      </div>
      <p
        className={twMerge(
          'pointer-events-none min-w-0 flex-1 pr-2 text-sm leading-5 text-primary-600 dark:text-primary-300 sm:leading-6',
          isCompact ? 'line-clamp-2' : 'whitespace-pre-wrap',
        )}
      >
        {file.summary || 'No AI summary available for this file.'}
      </p>
      {action ? (
        <div className="relative z-10 hidden shrink-0 sm:block">{action}</div>
      ) : null}
    </div>
  )
})

function SummaryMeasurementAction({ label }: { label: string }) {
  return (
    <span className="inline-flex h-7 shrink-0 items-center rounded px-2 text-sm font-semibold">
      {label}
    </span>
  )
}

function NoDocumentPreviewState({
  file,
  onAnalyzeFile,
  isAnalyzingFile,
  onDownloadFile,
}: {
  file: NormalizedFile
  onAnalyzeFile?: (file: NormalizedFile) => void
  isAnalyzingFile: boolean
  onDownloadFile: (file: NormalizedFile) => void
}) {
  const canDownload = hasDownloadUrl(file)

  return (
    <div className="flex min-h-0 flex-1 basis-0 items-center justify-center bg-primary-50 px-6 py-12 text-center dark:bg-primary-900/40">
      <div className="max-w-md">
        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg border border-primary-200 bg-white text-primary-400 dark:border-primary-700 dark:bg-primary-900 dark:text-primary-500">
          <FileText className="h-5 w-5" />
        </div>
        <div className="text-sm font-semibold text-primary-800 dark:text-primary-200">
          No inline preview for this file type
        </div>
        <div className="mt-4 flex flex-col justify-center gap-2 sm:flex-row">
          {onAnalyzeFile ? (
            <Button
              type="button"
              variant="outline-compact"
              onClick={() => onAnalyzeFile(file)}
              isLoading={isAnalyzingFile}
              disabled={isAnalyzingFile}
              className="h-8 justify-center gap-2 border-accent-200 bg-accent-50 px-3 text-sm font-medium text-accent-700 hover:border-accent-300 hover:bg-accent-100 dark:border-accent-800 dark:bg-accent-950/30 dark:text-accent-300 dark:hover:bg-accent-950/50"
            >
              <HiOutlineChatBubbleLeftRight className="h-4 w-4" />
              Analyze with Sweetspot AI
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline-compact"
            onClick={() => onDownloadFile(file)}
            disabled={!canDownload}
            title={canDownload ? undefined : 'Download unavailable'}
            className="h-8 justify-center gap-2 px-3 text-sm font-medium"
          >
            <Download className="h-4 w-4" />
            Download
          </Button>
        </div>
      </div>
    </div>
  )
}

function NoSolicitationDocumentsState({
  hasSourceFiles = false,
  emptyTitle = DEFAULT_EMPTY_DOCUMENTS_TITLE,
  emptyMessage = DEFAULT_EMPTY_DOCUMENTS_MESSAGE,
}: {
  hasSourceFiles?: boolean
  emptyTitle?: string
  emptyMessage?: string
}) {
  return (
    <div className="flex min-h-[180px] flex-col items-center justify-center rounded-lg border border-dashed border-primary-200 bg-primary-50 px-6 py-10 text-center dark:border-primary-700 dark:bg-primary-800/50">
      <div className="mb-3 rounded-md border border-primary-200 bg-white p-2 text-primary-400 dark:border-primary-700 dark:bg-primary-900 dark:text-primary-500">
        {hasSourceFiles ? (
          <Search className="h-5 w-5" />
        ) : (
          <FileText className="h-5 w-5" />
        )}
      </div>
      <div className="text-sm font-semibold text-primary-800 dark:text-primary-200">
        {hasSourceFiles ? 'No matching documents' : emptyTitle}
      </div>
      <p className="mt-1 max-w-md text-sm text-primary-500 dark:text-primary-400">
        {hasSourceFiles
          ? 'No documents match the current search and filters.'
          : emptyMessage}
      </p>
    </div>
  )
}

const SolicitationDocumentsSection = OpportunityDocumentsSection
const SolicitationDocumentsTab = OpportunityDocumentsTab

OpportunityDocumentsSection.displayName = 'OpportunityDocumentsSection'
OpportunityDocumentsTab.displayName = 'OpportunityDocumentsTab'

export {
  OpportunityDocumentsSection,
  OpportunityDocumentsTab,
  SolicitationDocumentsSection,
  SolicitationDocumentsTab,
}
export type {
  OpportunityDocumentsSectionProps,
  OpportunityDocumentsTabProps,
  SolicitationDocumentsSectionProps,
  SolicitationDocumentsTabProps,
}
